import React, { useRef, useEffect, useState, useContext, useCallback, useMemo } from 'react';
import { createChart, CrosshairMode, LineSeries } from 'lightweight-charts';
import { Plus } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { useBarsFetcher } from '../../hooks/useTradeStationQueries';
import { useUpdateAppSettings } from '../../hooks/useBackend';
import { observeElementResize } from '../../utils/observeElementResize';
import { MARKET_INDEX_SYMBOLS, MARKET_INDEX_CONFIG, MARKET_OVERVIEW_TIME_FRAMES, resolveMarketOverviewBarQuery } from '../../constants/marketIndices';
import {
  alignMarketOverviewBars,
  computeAverageSeries,
  dedupeBarsByTime,
  normalizeBarsToPercent,
  overlayBarsbackForSymbol,
  seriesEndPercent,
} from '../../utils/marketOverviewChart';

export { MARKET_INDEX_SYMBOLS, MARKET_OVERVIEW_TIME_FRAMES };

const AVERAGE_LINE_COLOR = '#ffffff';
/** lightweight-charts LineStyle.Dotted */
const LINE_STYLE_DOTTED = 1;

function formatLegendPercent(value) {
  if (!Number.isFinite(value)) return null;
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function getLineColor(symbol, colors) {
  const config = MARKET_INDEX_CONFIG[symbol];
  if (config) {
    // If color is a hex string, use it directly
    if (typeof config.color === 'string' && config.color.startsWith('#')) {
      return config.color;
    }
    // Otherwise map to theme colors
    return colors[config.color] || colors.primary;
  }
  return colors.primary;
}

function barTime(timestamp, unit) {
  if (!timestamp) return null;
  if (unit === 'Daily' || unit === 'Weekly' || unit === 'Monthly') {
    return String(timestamp).substring(0, 10);
  }
  const ms = Date.parse(timestamp);
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}

/**
 * Multi-symbol overlay chart (Market Overview: SPY / QQQ / DIA / IWM / BTCUSD + custom).
 * Normalized to % change from the first bar in the selected window.
 * Timeframe UI lives in the parent section header (shared TimeframeSelect).
 * Features:
 * - Interactive legend: color bar shows series color when enabled, grey when disabled
 * - Toggleable dotted average of enabled series (white)
 * - Series trimmed to a shared start so overlay lines begin together
 * - Add custom tickers via search modal
 * - Saves custom tickers to user settings
 */
const MultiLineChart = ({
  symbols = MARKET_INDEX_SYMBOLS,
  height = 300,
  fillHeight = false,
  timeframe: timeframeProp,
  lineWidth = 2,
  lineStyle = 0,
}) => {
  const { colors } = useTheme();
  const { user } = useContext(AuthContext);
  const fetchBars = useBarsFetcher();
  const { updateAppSettings } = useUpdateAppSettings();
  
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRefs = useRef({});
  const averageSeriesRef = useRef(null);
  const zeroLineRef = useRef(null);
  const fetchBarsRef = useRef(fetchBars);
  const rawBarsRef = useRef({});
  const normalizedDataRef = useRef({});
  
  const [internalTimeframe] = useState('1D');
  const timeframe = timeframeProp ?? internalTimeframe;
  const [loading, setLoading] = useState(true);
  const [chartReady, setChartReady] = useState(false);
  // End of each plotted series (and the average). Not the quote daily change:
  // TradeStation rolls that onto a new candle, so it stops matching the line.
  const [legendPercents, setLegendPercents] = useState({});
  
  // Get custom tickers from user settings
  const customTickers = useMemo(() => {
    return user?.appSettings?.customMarketOverviewTickers || [];
  }, [user?.appSettings?.customMarketOverviewTickers]);
  
  // Merge default and custom symbols
  const allSymbols = useMemo(() => {
    return [...MARKET_INDEX_SYMBOLS, ...customTickers];
  }, [customTickers]);
  
  // Track which series are enabled (visible)
  const [enabledSymbols, setEnabledSymbols] = useState(() => {
    // Initialize from localStorage or default to all enabled
    try {
      const stored = localStorage.getItem('pt:marketOverviewEnabled');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (_) {}
    return allSymbols.reduce((acc, s) => ({ ...acc, [s]: true }), {});
  });
  
  // Update enabled symbols when allSymbols changes (e.g., user adds custom ticker)
  useEffect(() => {
    setEnabledSymbols(prev => {
      const updated = { ...prev };
      allSymbols.forEach(symbol => {
        if (!(symbol in updated)) {
          updated[symbol] = true; // Enable new symbols by default
        }
      });
      return updated;
    });
  }, [allSymbols]);
  
  // Persist enabled symbols to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('pt:marketOverviewEnabled', JSON.stringify(enabledSymbols));
    } catch (_) {}
  }, [enabledSymbols]);

  const [averageEnabled, setAverageEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem('pt:marketOverviewAverageEnabled');
      if (stored != null) return JSON.parse(stored) !== false;
    } catch (_) {}
    return true;
  });

  const enabledSymbolsRef = useRef(enabledSymbols);
  const averageEnabledRef = useRef(averageEnabled);
  enabledSymbolsRef.current = enabledSymbols;
  averageEnabledRef.current = averageEnabled;

  useEffect(() => {
    try {
      localStorage.setItem('pt:marketOverviewAverageEnabled', JSON.stringify(averageEnabled));
    } catch (_) {}
  }, [averageEnabled]);
  
  const toggleSymbol = useCallback((symbol) => {
    setEnabledSymbols(prev => ({ ...prev, [symbol]: !prev[symbol] }));
  }, []);

  const toggleAverage = useCallback(() => {
    setAverageEnabled((prev) => !prev);
  }, []);

  const applyAverageSeries = useCallback((enabledMap, showAverage) => {
    const averageData = computeAverageSeries(normalizedDataRef.current, enabledMap);
    const nextPercents = {};
    Object.entries(normalizedDataRef.current).forEach(([symbol, points]) => {
      const pct = seriesEndPercent(points);
      if (pct != null) nextPercents[symbol] = pct;
    });
    const averagePct = seriesEndPercent(averageData);
    if (averagePct != null) nextPercents.AVERAGE = averagePct;
    setLegendPercents(nextPercents);

    if (!averageSeriesRef.current) return;
    if (!showAverage) {
      averageSeriesRef.current.setData([]);
      averageSeriesRef.current.applyOptions({ visible: false });
      return;
    }
    averageSeriesRef.current.setData(averageData);
    averageSeriesRef.current.applyOptions({ visible: averageData.length > 0 });
  }, []);
  
  const handleAddTicker = useCallback(() => {
    window.dispatchEvent(new CustomEvent('ticker:add-to-watchlist', {
      detail: {
        onAdd: async (ticker) => {
          if (!ticker) return;
          // Don't add duplicates
          if (allSymbols.includes(ticker)) return;
          
          // Save to appSettings
          const newCustomTickers = [...customTickers, ticker];
          try {
            if (user?.authToken) {
              await updateAppSettings(user.authToken, {
                customMarketOverviewTickers: newCustomTickers,
              });
            }
          } catch (err) {
            console.error('Failed to save custom ticker:', err);
          }
        }
      }
    }));
  }, [allSymbols, customTickers, user, updateAppSettings]);
  
  const removeCustomTicker = useCallback(async (ticker) => {
    const newCustomTickers = customTickers.filter(t => t !== ticker);
    try {
      if (user?.authToken) {
        await updateAppSettings(user.authToken, {
          customMarketOverviewTickers: newCustomTickers,
        });
      }
    } catch (err) {
      console.error('Failed to remove custom ticker:', err);
    }
  }, [customTickers, user, updateAppSettings]);

  fetchBarsRef.current = fetchBars;

  const symbolKey = allSymbols.join(',');

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const resolvedHeight = fillHeight
      ? (el.clientHeight > 0 ? el.clientHeight : height)
      : height;

    const chart = createChart(el, {
      width: el.clientWidth,
      height: resolvedHeight,
      layout: {
        background: { color: colors.bgCard },
        textColor: colors.textMuted,
      },
      grid: {
        // Keep axis labels; hide grid/axis lines for a cleaner overlay.
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: {
        borderVisible: false,
        timeVisible: timeframe === '1D' || timeframe === '1W',
        secondsVisible: false,
      },
      // Normal, not Magnet: the crosshair follows the pointer instead of snapping to a line.
      crosshair: { mode: CrosshairMode.Normal },
    });

    const nextSeries = {};
    symbolKey.split(',').filter(Boolean).forEach((symbol) => {
      nextSeries[symbol] = chart.addSeries(LineSeries, {
        color: getLineColor(symbol, colors),
        lineWidth,
        lineStyle,
        // Keep % labels on the price axis; hide last-price horizontals that clutter overlays.
        priceLineVisible: false,
        lastValueVisible: true,
        priceFormat: {
          type: 'custom',
          formatter: (price) => `${Number(price).toFixed(2)}%`,
        },
      });
    });

    // Add average series (white dotted line; toggleable via legend)
    const avgSeries = chart.addSeries(LineSeries, {
      color: AVERAGE_LINE_COLOR,
      lineWidth,
      lineStyle: LINE_STYLE_DOTTED,
      priceLineVisible: false,
      lastValueVisible: true,
      priceFormat: {
        type: 'custom',
        formatter: (price) => `${Number(price).toFixed(2)}%`,
      },
    });

    chartRef.current = chart;
    seriesRefs.current = nextSeries;
    averageSeriesRef.current = avgSeries;
    zeroLineRef.current = avgSeries.createPriceLine({
      price: 0,
      color: colors.border || 'rgba(255,255,255,0.35)',
      lineWidth: 1,
      lineStyle: 2, // Dashed
      axisLabelVisible: true,
      title: '0%',
    });
    setChartReady(true);

    const disconnectResize = observeElementResize(el, () => {
      if (chartRef.current && containerRef.current) {
        const next = { width: containerRef.current.clientWidth };
        if (fillHeight && containerRef.current.clientHeight > 0) {
          next.height = containerRef.current.clientHeight;
        }
        chartRef.current.applyOptions(next);
      }
    });

    return () => {
      disconnectResize?.();
      setChartReady(false);
      chart.remove();
      chartRef.current = null;
      seriesRefs.current = {};
      averageSeriesRef.current = null;
      zeroLineRef.current = null;
    };
    // Recreate only when the chart chrome or symbol list actually changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, fillHeight, symbolKey, colors.bgCard, colors.textMuted, colors.border]);

  useEffect(() => {
    if (!chartReady || !chartRef.current) return undefined;

    const tf = MARKET_OVERVIEW_TIME_FRAMES.find((t) => t.key === timeframe);
    if (!tf) return undefined;

    const symbolList = symbolKey.split(',').filter(Boolean);
    let cancelled = false;
    setLoading(true);
    setLegendPercents({});

    Promise.all(
      symbolList.map((symbol) => {
        const query = resolveMarketOverviewBarQuery(tf);
        return fetchBarsRef.current({
          ticker: symbol,
          interval: tf.interval,
          unit: tf.unit,
          sessionTemplate: 'USEQPreAndPost',
          ...query,
          ...(query.barsback != null
            ? { barsback: overlayBarsbackForSymbol(symbol, { ...tf, barsback: query.barsback }) }
            : {}),
        })
          .then((data) => {
            const bars = dedupeBarsByTime(
              (data?.Bars || [])
                .map((b) => {
                  const time = barTime(b.TimeStamp, tf.unit);
                  const close = parseFloat(b.Close);
                  if (time == null || !Number.isFinite(close)) return null;
                  return { time, close };
                })
                .filter(Boolean)
                .sort((a, b) => (a.time < b.time ? -1 : 1)),
            );
            return { symbol, bars };
          })
          .catch((err) => {
            console.error(`Failed to load bars for ${symbol}:`, err);
            return { symbol, bars: [] };
          });
      })
    ).then((results) => {
      if (cancelled) return;
      chartRef.current?.applyOptions({
        timeScale: {
          timeVisible: tf.unit === 'Minute',
          secondsVisible: false,
        },
      });
      
      // Store raw bars, then drop prints before 12:00 AM ET of the session day.
      // Bitcoin keeps overnight/weekend bars after equities stop.
      const barsBySymbol = {};
      results.forEach(({ symbol, bars }) => {
        barsBySymbol[symbol] = bars;
        rawBarsRef.current[symbol] = bars;
      });
      const alignedBars = alignMarketOverviewBars(barsBySymbol, tf);

      Object.entries(alignedBars).forEach(([symbol, bars]) => {
        const series = seriesRefs.current[symbol];
        if (!series) return;
        if (!bars.length) {
          series.setData([]);
          normalizedDataRef.current[symbol] = [];
          return;
        }

        // Start at 0% from the first print in the midnight-aligned window.
        const baseline = bars[0].close;
        if (!baseline) {
          series.setData([]);
          normalizedDataRef.current[symbol] = [];
          return;
        }
        const normalized = normalizeBarsToPercent(bars, baseline);
        normalizedDataRef.current[symbol] = normalized;
        series.setData(normalized);
      });

      // Visibility / Average are owned by the toggle effect — use latest refs here.
      applyAverageSeries(enabledSymbolsRef.current, averageEnabledRef.current);

      chartRef.current?.timeScale().fitContent();
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      console.error('Failed to load chart data:', err);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // enabledSymbols / averageEnabled only toggle visibility — do not refetch or show Loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartReady, timeframe, symbolKey, applyAverageSeries]);

  // Update series / average visibility when toggles change (no refetch)
  useEffect(() => {
    Object.entries(seriesRefs.current).forEach(([symbol, series]) => {
      if (series && series.applyOptions) {
        series.applyOptions({
          visible: enabledSymbols[symbol] !== false,
        });
      }
    });
    applyAverageSeries(enabledSymbols, averageEnabled);
  }, [enabledSymbols, averageEnabled, applyAverageSeries]);

  // Apply stroke prefs without recreating the chart / refetching bars.
  useEffect(() => {
    Object.values(seriesRefs.current).forEach((series) => {
      series?.applyOptions?.({
        lineWidth,
        lineStyle,
        priceLineVisible: false,
        lastValueVisible: true,
      });
    });
    averageSeriesRef.current?.applyOptions?.({
      lineWidth,
      // Keep Average dotted so it stays visually distinct from index lines.
      lineStyle: LINE_STYLE_DOTTED,
      priceLineVisible: false,
      lastValueVisible: true,
    });
  }, [lineWidth, lineStyle, chartReady]);

  return (
    <div
      style={{
        width: '100%',
        // Flex into whatever the parent column leaves after its header, so the legend below
        // the chart is never pushed out of view.
        flex: fillHeight ? '1 1 0' : undefined,
        height: fillHeight ? '100%' : undefined,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <div
        style={{
          position: 'relative',
          flex: fillHeight ? 1 : undefined,
          minHeight: fillHeight ? 0 : undefined,
        }}
      >
        <div
          ref={containerRef}
          style={{ width: '100%', height: fillHeight ? '100%' : height }}
        />
        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${colors.bgCard}cc`,
              zIndex: 10,
              pointerEvents: 'none',
            }}
          >
            <div style={{ color: colors.textMuted }}>Loading...</div>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 12,
          // In fill mode the legend is the last row before the page edge, so reserve room
          // under it — the flexed canvas gives up the height instead of crowding the bottom.
          marginBottom: fillHeight ? 16 : 0,
          justifyContent: 'center',
          flexWrap: 'wrap',
          flexShrink: 0,
          alignItems: 'center',
        }}
      >
        {/* Individual series */}
        {allSymbols.map((symbol) => {
          const change = legendPercents[symbol];
          const changeLabel = formatLegendPercent(change);
          const hasChange = changeLabel != null;
          const isEnabled = enabledSymbols[symbol] !== false;
          const isCustom = customTickers.includes(symbol);
          const config = MARKET_INDEX_CONFIG[symbol];
          const displayName = config?.name || symbol;
          
          return (
            <div
              key={symbol}
              data-testid={`market-overview-legend-${symbol}`}
              data-enabled={isEnabled ? 'true' : 'false'}
              onClick={() => toggleSymbol(symbol)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSymbol(symbol);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={isEnabled}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: 6,
                backgroundColor: colors.bgSecondary,
                transition: 'all 0.2s ease',
              }}
              title={`Click to ${isEnabled ? 'hide' : 'show'} ${displayName}`}
            >
              {/* Color bar: series color when enabled, grey when disabled */}
              <div
                data-testid={`market-overview-legend-bar-${symbol}`}
                style={{
                  width: 12,
                  height: 3,
                  backgroundColor: isEnabled ? getLineColor(symbol, colors) : colors.textMuted,
                  transition: 'background-color 0.2s ease',
                }}
              />
              
              {/* Symbol name */}
              <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>{displayName}</span>
              
              {/* Change % */}
              {hasChange && (
                <span
                  data-testid={`market-overview-legend-pct-${symbol}`}
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: change >= 0 ? colors.success : colors.danger,
                  }}
                >
                  {changeLabel}
                </span>
              )}
              
              {/* Remove button for custom tickers */}
              {isCustom && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeCustomTicker(symbol);
                  }}
                  style={{
                    marginLeft: 4,
                    padding: 2,
                    border: 'none',
                    background: 'transparent',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    fontSize: 11,
                    lineHeight: 1,
                  }}
                  title={`Remove ${symbol}`}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}

        {/* Average line — toggleable, dotted; sits at end before Add */}
        <div
          data-testid="market-overview-legend-AVERAGE"
          data-enabled={averageEnabled ? 'true' : 'false'}
          onClick={toggleAverage}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleAverage();
            }
          }}
          role="button"
          tabIndex={0}
          aria-pressed={averageEnabled}
          title={`Click to ${averageEnabled ? 'hide' : 'show'} Average`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: 6,
            backgroundColor: colors.bgSecondary,
            transition: 'all 0.2s ease',
          }}
        >
          <div
            data-testid="market-overview-legend-bar-AVERAGE"
            style={{
              width: 14,
              height: 0,
              borderTop: `2px dotted ${averageEnabled ? AVERAGE_LINE_COLOR : colors.textMuted}`,
              transition: 'border-color 0.2s ease',
            }}
          />
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>Average</span>
          {formatLegendPercent(legendPercents.AVERAGE) && (
            <span
              data-testid="market-overview-legend-pct-AVERAGE"
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: legendPercents.AVERAGE >= 0 ? colors.success : colors.danger,
              }}
            >
              {formatLegendPercent(legendPercents.AVERAGE)}
            </span>
          )}
        </div>
        
        {/* Add button */}
        <button
          type="button"
          onClick={handleAddTicker}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 8px',
            borderRadius: 6,
            border: `1px dashed ${colors.border}`,
            backgroundColor: 'transparent',
            color: colors.textMuted,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colors.primary;
            e.currentTarget.style.color = colors.primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = colors.border;
            e.currentTarget.style.color = colors.textMuted;
          }}
          title="Add custom ticker"
        >
          <Plus size={14} />
          <span>Add</span>
        </button>
      </div>
    </div>
  );
};

export default MultiLineChart;
